import React from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';

import {
    CellRendererParams,
    InscriptionWithMetadata,
} from 'routes/BudgetModule/config/types';
import { INSCRIPTION_ID } from 'routes/BudgetModule/config/constants';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';

import { styled } from '@mui/material/styles';
import { Badge, Box } from '@mui/material';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { useCommentsPerInscriptionContext } from '../CommentPerInscriptionContext';

const StyBadge = styled(Badge, {
    name: 'StyBadge',
})({
    '& > .MuiBadge-badge': {
        backgroundColor: 'transparent',
        top: '42%',
        right: '50%',
    },
});

const StyBox = styled(Box, {
    name: 'StyBox',
})({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

export type SeeDetailsCellRendererParams =
    CellRendererParams<InscriptionWithMetadata> & {
        setSelectedDetailInscription: React.Dispatch<
            React.SetStateAction<InscriptionT | undefined>
        >;
    };

export default function SeeDetailsCellRenderer({
    data,
    setSelectedDetailInscription,
}: SeeDetailsCellRendererParams): React.ReactElement | null {
    const { t } = useTranslation();
    const commentsPerInscription = useCommentsPerInscriptionContext();

    if (!data) {
        return <span />;
    }
    const { inscription } = data;
    const count = commentsPerInscription?.[inscription[INSCRIPTION_ID]] ?? 0;

    const toggleDrawer = (event: React.MouseEvent | React.KeyboardEvent) => {
        if (
            event.type === 'keydown' &&
            'key' in event &&
            (event.key === 'Tab' || event.key === 'Shift')
        ) {
            return;
        }
        trackEventBudget(TRACKABLE_EVENTS.budget.general.detailsOpened);
        setSelectedDetailInscription(inscription);
    };

    return (
        <StyBox>
            <Tooltip title={t('budget.commentButton')}>
                <IconButton
                    color="primary"
                    onClick={(e) => toggleDrawer(e)}
                    size="large"
                    centerRipple
                >
                    <StyBadge
                        badgeContent={count}
                        color="error"
                        max={9}
                        overlap="circular"
                    >
                        <ChatBubbleIcon />
                    </StyBadge>
                </IconButton>
            </Tooltip>
        </StyBox>
    );
}
